import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { type QuoteType, RequestLogsQuery } from '@engine/models/admin'

import {
  getRangeOptions,
  RANGE_MINUTES,
  type RangeKey,
} from '#utils/timeRanges'

import { AdminFilters } from './adminFilters/AdminFiltersPanel'
import { FilterInput } from './adminFilters/FilterInput'
import { FilterRow } from './adminFilters/FilterRow'
import { FilterSelect } from './adminFilters/FilterSelect'
import { ScacTypeahead } from './ScacTypeahead'

interface LocalFiltersState {
  externalId: string
  requestId: string
  trackingNumber: string
  scac: string
  status: string
  range: RangeKey
  customFrom: string
  customTo: string
  errorType: string
  quoteType: QuoteType
}

export const useRequestLogFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [localFilters, setLocalFilters] = useState<LocalFiltersState>({
    externalId: searchParams.get('externalId') || '',
    requestId: searchParams.get('requestId') || '',
    trackingNumber: searchParams.get('trackingNumber') || '',
    status: searchParams.get('status') || '',
    scac: searchParams.get('scac') || '',
    range: (searchParams.get('range') as RangeKey) || 'all',
    customFrom: searchParams.get('customFrom') || '',
    customTo: searchParams.get('customTo') || '',
    errorType: searchParams.get('errorType') || '',
    quoteType: (searchParams.get('quoteType') as QuoteType) || 'all',
  })

  // Convert local datetime strings to UTC ISO format for API
  const filters = useMemo<RequestLogsQuery>(() => {
    const now = new Date()
    let startDate: string | undefined
    let endDate: string | undefined

    if (localFilters.range === 'all') {
      // No date filters for 'all'
      startDate = undefined
      endDate = undefined
    } else if (localFilters.range === 'custom') {
      startDate = localFilters.customFrom
        ? new Date(localFilters.customFrom).toISOString()
        : undefined
      endDate = localFilters.customTo
        ? new Date(localFilters.customTo).toISOString()
        : undefined
    } else {
      const minutes = RANGE_MINUTES[localFilters.range]
      const from = new Date(now.getTime() - minutes * 60 * 1_000)
      startDate = from.toISOString()
      endDate = now.toISOString()
    }

    return {
      externalId: localFilters.externalId || undefined,
      requestId: localFilters.requestId || undefined,
      trackingNumber: localFilters.trackingNumber || undefined,
      status: localFilters.status || undefined,
      scac: localFilters.scac || undefined,
      startDate,
      endDate,
      errorType: localFilters.errorType || undefined,
      quoteType:
        localFilters.quoteType === 'all' ? undefined : localFilters.quoteType,
    }
  }, [localFilters])

  // Sync filters to URL query params
  useEffect(() => {
    const params = new URLSearchParams()
    if (localFilters.externalId)
      params.set('externalId', localFilters.externalId)
    if (localFilters.requestId) params.set('requestId', localFilters.requestId)
    if (localFilters.trackingNumber)
      params.set('trackingNumber', localFilters.trackingNumber)
    if (localFilters.status) params.set('status', localFilters.status)
    if (localFilters.scac) params.set('scac', localFilters.scac)
    if (localFilters.range) params.set('range', localFilters.range)
    if (localFilters.customFrom)
      params.set('customFrom', localFilters.customFrom)
    if (localFilters.customTo) params.set('customTo', localFilters.customTo)
    if (localFilters.errorType) params.set('errorType', localFilters.errorType)
    if (localFilters.quoteType !== 'all')
      params.set('quoteType', localFilters.quoteType)
    setSearchParams(params, { replace: true })
  }, [localFilters, setSearchParams])

  return { filters, localFilters, setLocalFilters }
}

interface TracesFiltersProps {
  isLoading: boolean
  onRefresh: () => void
  localFilters: LocalFiltersState
  setLocalFilters: (filters: LocalFiltersState) => void
  errorTypes: string[]
  statuses?: string[]
  showTrackingNumber?: boolean
  showQuoteType?: boolean
}

export const RequestLogFilters = ({
  isLoading,
  onRefresh,
  localFilters,
  setLocalFilters,
  errorTypes,
  statuses = ['ok', 'error', 'pending'],
  showTrackingNumber = false,
  showQuoteType = false,
}: TracesFiltersProps) => {
  return (
    <AdminFilters isLoading={isLoading} onRefresh={onRefresh}>
      <FilterRow>
        <FilterInput
          label="External ID"
          value={localFilters.externalId}
          onChange={value =>
            setLocalFilters({ ...localFilters, externalId: value })
          }
          placeholder="Search by external ID..."
        />
        <FilterInput
          label="Request ID"
          value={localFilters.requestId}
          onChange={value =>
            setLocalFilters({ ...localFilters, requestId: value })
          }
          placeholder="Search by request ID..."
        />
        <ScacTypeahead
          value={localFilters.scac}
          onChange={value => setLocalFilters({ ...localFilters, scac: value })}
        />
        {showTrackingNumber && (
          <FilterInput
            label="PRO Number"
            value={localFilters.trackingNumber}
            onChange={value =>
              setLocalFilters({ ...localFilters, trackingNumber: value })
            }
            placeholder="Search by PRO number..."
          />
        )}
      </FilterRow>
      <FilterRow>
        {showQuoteType && (
          <FilterSelect
            label="Quote Type"
            value={localFilters.quoteType}
            onChange={value =>
              setLocalFilters({
                ...localFilters,
                quoteType: value as QuoteType,
              })
            }
            options={[
              { value: 'all', label: 'All' },
              { value: 'volume', label: 'Volume' },
              { value: 'dynamic', label: 'Dynamic' },
            ]}
          />
        )}
        {statuses.length > 0 && (
          <FilterSelect
            label="Status"
            value={localFilters.status}
            onChange={value =>
              setLocalFilters({ ...localFilters, status: value })
            }
            options={[
              { value: '', label: 'All' },
              ...statuses.map(status => ({
                value: status,
                label: status.charAt(0).toUpperCase() + status.slice(1),
              })),
            ]}
          />
        )}
        {localFilters.status === 'error' && errorTypes.length > 0 && (
          <FilterSelect
            className="basis-1/6"
            label="Error Type"
            value={localFilters.errorType}
            onChange={value =>
              setLocalFilters({ ...localFilters, errorType: value })
            }
            options={[
              { value: '', label: 'All' },
              ...errorTypes.map(type => ({
                value: type,
                label: type.charAt(0).toUpperCase() + type.slice(1),
              })),
            ]}
          />
        )}
        <FilterSelect
          label="Time range"
          value={localFilters.range}
          onChange={value =>
            setLocalFilters({ ...localFilters, range: value as RangeKey })
          }
          options={getRangeOptions()}
        />
        {localFilters.range === 'custom' ? (
          <>
            <FilterInput
              label="From"
              value={localFilters.customFrom}
              onChange={value =>
                setLocalFilters({ ...localFilters, customFrom: value })
              }
              type="datetime-local"
            />
            <FilterInput
              label="To"
              value={localFilters.customTo}
              onChange={value =>
                setLocalFilters({ ...localFilters, customTo: value })
              }
              type="datetime-local"
            />
          </>
        ) : null}
      </FilterRow>
    </AdminFilters>
  )
}
